import type { $Fetch } from 'ofetch';
import type {
  Docente, CreateDocenteCommand, UpdateDocenteCommand, DocenteAssignment,
  CreateDocenteAssignmentCommand, TitulacaoAcademica, DocenteStatus,
} from '@/domain/model/Docente';
import type { Page } from '@/domain/model/Page';
import type { DocenteGateway } from '@/application/port/out/DocenteGateway';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const PATH = '/api/v1/docentes';

export class HttpDocenteGateway implements DocenteGateway {
  constructor(private readonly client: $Fetch) {}

  async list(p: {
    name?: string;
    titulacao?: TitulacaoAcademica;
    status?: DocenteStatus;
    page?: number;
    size?: number;
  }): Promise<Page<Docente>> {
    const params: Record<string, string | number> = {};
    if (p.name) params['name'] = p.name;
    if (p.titulacao) params['titulacao'] = p.titulacao;
    if (p.status) params['status'] = p.status;
    if (typeof p.page === 'number') params['page'] = p.page;
    if (typeof p.size === 'number') params['size'] = p.size;
    const res = await this.client<ApiResponse<SpringPage<Docente>>>(PATH, { method: 'GET', params });
    assertApiSuccess(res);
    const d = res.data;
    return { content: d.content, number: d.number, size: d.size, totalElements: d.totalElements, totalPages: d.totalPages, first: d.first, last: d.last };
  }

  async get(id: string): Promise<Docente> {
    const res = await this.client<ApiResponse<Docente>>(`${PATH}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async create(command: CreateDocenteCommand): Promise<Docente> {
    const res = await this.client<ApiResponse<Docente>>(PATH, { method: 'POST', body: command });
    assertApiSuccess(res);
    return res.data;
  }

  // Fix audit FASE G P0: backend usa DocenteUpdateRequest (sem CPF) por LGPD
  async update(id: string, command: UpdateDocenteCommand): Promise<Docente> {
    const res = await this.client<ApiResponse<Docente>>(
      `${PATH}/${encodeURIComponent(id)}`, { method: 'PUT', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  // Fix audit F.2 P0: backend usa @DeleteMapping (archive lógico).
  async archive(id: string): Promise<Docente> {
    const res = await this.client<ApiResponse<Docente>>(
      `${PATH}/${encodeURIComponent(id)}`, { method: 'DELETE' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async listAssignments(docenteId: string): Promise<DocenteAssignment[]> {
    const res = await this.client<ApiResponse<DocenteAssignment[]>>(
      `${PATH}/${encodeURIComponent(docenteId)}/assignments`, { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async assign(docenteId: string, command: CreateDocenteAssignmentCommand): Promise<DocenteAssignment> {
    const res = await this.client<ApiResponse<DocenteAssignment>>(
      `${PATH}/${encodeURIComponent(docenteId)}/assignments`,
      { method: 'POST', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async unassign(docenteId: string, assignmentId: string): Promise<void> {
    // Backend retorna 204 No Content (DocenteController.unassign → ResponseEntity.noContent()).
    // Não chamar assertApiSuccess: body é vazio e o helper crasharia tentando ler `res.success`.
    // Erros HTTP 4xx/5xx já viram DomainError no ofetchClient global.
    await this.client(
      `${PATH}/${encodeURIComponent(docenteId)}/assignments/${encodeURIComponent(assignmentId)}`,
      { method: 'DELETE' },
    );
  }
}
